import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Loader2, Lock, CreditCard, Mail, LogOut, HelpCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PaywallGuard
 * Hård betalvägg som låser admin-konton äldre än 14 dagar utan aktiv prenumeration.
 * - Visas ENDAST för role === 'admin'
 * - Superadmin och sales kringgår betalväggen (operio-personal)
 * - Worker, customer och oinloggade påverkas inte
 */
const PaywallGuard = ({ children }) => {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceSent, setInvoiceSent] = useState(null);

  const fetchStatus = useCallback(async () => {
    // Bara admin-roll har potentiellt betalvägg
    if (!user || user.role !== 'admin') {
      setLoading(false);
      return;
    }
    try {
      const res = await api.paymentRequired();
      setStatus(res.data);
    } catch (err) {
      console.error('Paywall status fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handlePayNow = async () => {
    setPayLoading(true);
    try {
      const res = await api.payNowCheckout(window.location.origin);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Kunde inte starta betalningen — kontakta support.');
        setPayLoading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa betalsession');
      setPayLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    setInvoiceLoading(true);
    try {
      const res = await api.sendInvoiceNow();
      setInvoiceSent(res.data);
      toast.success('Faktura skickad till din e-post — betala inom 14 dagar.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skicka faktura');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Endast admin omfattas — alla andra roller passerar rakt igenom
  if (!user || user.role !== 'admin') {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 text-[#E8613A] animate-spin" />
      </div>
    );
  }

  // Inte spärrad — visa appen som vanligt
  if (!status?.required) {
    return children;
  }

  // Betalvägg aktiv
  return (
    <PaywallScreen
      status={status}
      onPayNow={handlePayNow}
      onSendInvoice={handleSendInvoice}
      onLogout={logout}
      payLoading={payLoading}
      invoiceLoading={invoiceLoading}
      invoiceSent={invoiceSent}
    />
  );
};

const PaywallScreen = ({ status, onPayNow, onSendInvoice, onLogout, payLoading, invoiceLoading, invoiceSent }) => {
  const monthly = Number(status?.monthly_price || 0).toLocaleString('sv-SE');
  const vat = Number(status?.vat_amount || 0).toLocaleString('sv-SE');
  const total = Number(status?.total_with_vat || 0).toLocaleString('sv-SE');
  const planLabel = status?.plan_label || status?.plan_id || 'Operio';
  const daysOverdue = status?.days_overdue ?? 0;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-[#0F0E0D] flex items-center justify-center px-4 py-6 overflow-y-auto"
      data-testid="paywall-overlay"
    >
      {/* Bakgrundsmönster */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '32px 32px',
      }} />

      <div className="relative max-w-2xl w-full">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <img src="/operio-logo-2.png" alt="Operio" className="w-8 h-8" />
          <span className="text-xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Operio
          </span>
        </div>

        <div className="bg-[#1A1816] border border-[#3A3530] rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#E8613A] to-[#C94824] px-6 sm:px-10 py-7 text-white">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center backdrop-blur-sm">
                <Lock size={18} strokeWidth={2.4} />
              </div>
              <span className="text-xs uppercase tracking-[0.18em] font-semibold opacity-90">
                Provperioden är slut
              </span>
            </div>
            <h1
              className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Aktivera din prenumeration för att fortsätta
            </h1>
            <p className="text-sm sm:text-base text-white/90 mt-2 leading-relaxed">
              {daysOverdue > 0
                ? `Din 14-dagars gratis provperiod löpte ut för ${daysOverdue} dag${daysOverdue === 1 ? '' : 'ar'} sedan. `
                : 'Din 14-dagars gratis provperiod är slut. '}
              Slutför betalningen så är du tillbaka på sekunden.
            </p>
          </div>

          {/* Body */}
          <div className="px-6 sm:px-10 py-7 space-y-6">
            {/* Plan-summering */}
            <div className="rounded-xl border border-[#3A3530] bg-[#221F1C] p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-white/50 font-semibold">Ditt paket</p>
                  <p className="text-lg font-semibold text-white mt-0.5" data-testid="paywall-plan-label">
                    {planLabel}
                  </p>
                  {status?.company_name && (
                    <p className="text-xs text-white/40 mt-0.5">{status.company_name}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-white/50 font-semibold">Månadspris</p>
                  <p className="text-2xl font-bold text-white mt-0.5" data-testid="paywall-monthly-price">
                    {monthly} kr
                  </p>
                  <p className="text-[10px] text-white/40 mt-0.5">exkl. moms</p>
                </div>
              </div>
              <div className="border-t border-[#3A3530] pt-3 flex items-center justify-between text-xs text-white/70">
                <span>Moms 25 %</span>
                <span data-testid="paywall-vat">{vat} kr</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-sm font-semibold text-white">
                <span>Att betala / månad</span>
                <span data-testid="paywall-total">{total} kr</span>
              </div>
            </div>

            {/* Faktura skickad-bekräftelse */}
            {invoiceSent && (
              <div
                className="rounded-xl border border-green-700/50 bg-green-950/30 p-4 text-sm text-green-200 flex items-start gap-3"
                data-testid="paywall-invoice-sent"
              >
                <Mail size={16} className="flex-shrink-0 mt-0.5 text-green-400" />
                <div className="flex-1">
                  <p className="font-semibold">Faktura skickad!</p>
                  <p className="text-xs text-green-300/80 mt-0.5">
                    Vi har mejlat en faktura på <strong>{Number(invoiceSent.amount_due || 0).toLocaleString('sv-SE')} kr</strong>.
                    Du har 14 dagar på dig att betala. När betalningen registreras låses kontot upp automatiskt.
                  </p>
                  {invoiceSent.hosted_invoice_url && (
                    <a
                      href={invoiceSent.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-400 hover:text-green-300 underline mt-2"
                      data-testid="paywall-invoice-link"
                    >
                      Öppna fakturan <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Action-knappar */}
            <div className="space-y-3">
              <button
                onClick={onPayNow}
                disabled={payLoading}
                data-testid="paywall-pay-now-btn"
                className="w-full h-14 bg-[#E8613A] hover:bg-[#D4532F] active:bg-[#BC4525] text-white font-semibold rounded-xl flex items-center justify-center gap-2.5 transition-colors disabled:opacity-60 disabled:cursor-wait shadow-lg shadow-[#E8613A]/20"
              >
                {payLoading ? (
                  <><Loader2 size={18} className="animate-spin" /> Skapar betalsession…</>
                ) : (
                  <><CreditCard size={18} strokeWidth={2.2} /> Betala nu med kort</>
                )}
              </button>

              <button
                onClick={onSendInvoice}
                disabled={invoiceLoading || !!invoiceSent}
                data-testid="paywall-send-invoice-btn"
                className="w-full h-12 bg-transparent border border-[#3A3530] hover:bg-[#221F1C] hover:border-white/30 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              >
                {invoiceLoading ? (
                  <><Loader2 size={16} className="animate-spin" /> Skickar…</>
                ) : invoiceSent ? (
                  <><Mail size={16} /> Faktura skickad ✓</>
                ) : (
                  <><Mail size={16} /> Skicka faktura via e-post</>
                )}
              </button>
            </div>

            {/* Info-rad */}
            <div className="flex items-start gap-2.5 text-xs text-white/50 leading-relaxed pt-1">
              <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
              <p>
                Alla rapporter, kunder, projekt och fakturor finns kvar i kontot. Så snart betalningen är genomförd är allt tillbaka som vanligt.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-[#3A3530] bg-[#0F0E0D] px-6 sm:px-10 py-4 flex flex-wrap items-center justify-between gap-3">
            <a
              href="mailto:support@operio.nu"
              className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
              data-testid="paywall-support-link"
            >
              <HelpCircle size={13} />
              Kontakta support
            </a>
            <button
              onClick={onLogout}
              data-testid="paywall-logout-btn"
              className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
            >
              <LogOut size={13} />
              Logga ut
            </button>
          </div>
        </div>

        <p className="text-center text-[11px] text-white/30 mt-5">
          Operio — Sveriges modernaste plattform för bygg, mark & fält
        </p>
      </div>
    </div>
  );
};

export default PaywallGuard;
